import { lazy, Suspense, useLayoutEffect } from 'react';
import { usePreferencesUIStore } from '@/store/preferencesUIStore';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import ScrollProgress from '@/components/ui/ScrollProgress';
import { useAppStore } from '../store/appStore';
import { useUIStore } from '../store/uiStore';
const PreferencesModal = lazy(() => import('@/features/preferences/PreferencesModal'));
import EntitlementBanner from '@/components/EntitlementBanner';

export default function AppLayout() {
  const location = useLocation();
  const { isSidebarExpanded, setSidebarExpanded } = useAppStore();
  const preferencesOpen = usePreferencesUIStore((state) => state.isOpen);
  const headerVisible = useUIStore((state: any) => state.headerVisible);

  const title = (() => {
    if (location.pathname === '/chat') return 'AI Chat';
    if (location.pathname === '/chat/legacy') return 'Lex Assistant';
    if (location.pathname === '/agents') return 'Agents';
    if (location.pathname === '/accounting' || location.pathname.startsWith('/accounting/')) return 'Accounting';
    if (location.pathname === '/chat_test_runner') return 'Chat Test Runner';
    if (location.pathname === '/system-monitor/ws-log') return 'WebSocket Logs';
    if (location.pathname === '/system-monitor') return 'EdgeLex System Monitor';
    return '';
  })();

  // Hide global header based on UI store (controlled by EdgeDocsEditorPage)
  const hideHeader = !headerVisible;

  // Check if we're on an EdgeDocs editor page or EdgeMail client (needs full-bleed layout)
  const isEdgeDocsEditor = location.pathname.includes('/editor') || location.pathname.includes('/dms/editor');
  const isEdgeMailClient = location.pathname === '/email' || location.pathname.startsWith('/email/');
  const isCalendarPage = location.pathname === '/calendar' || location.pathname.startsWith('/calendar/');
  const isLexRuntimeWorkspace = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
  const isAccountingSurface = location.pathname === '/accounting' || location.pathname.startsWith('/accounting/');
  useLayoutEffect(() => {
    if (isLexRuntimeWorkspace) setSidebarExpanded(false);
  }, [isLexRuntimeWorkspace, setSidebarExpanded]);
  const routeDocumentId = location.pathname.match(/\/(?:documents|dms\/editor)\/([0-9a-f-]{36})/i)?.[1];
  const captureSurface = isEdgeDocsEditor ? 'dms_editor' : isEdgeMailClient ? 'edgemail_message_surface' : location.pathname.startsWith('/matters') ? 'matter_workspace' : 'edgelex_application_surface';

  return (
    <div className="flex h-dvh overflow-hidden bg-[var(--lex-surface-app)] text-[var(--lex-text-primary)]">
      <Sidebar />
      <div className="relative z-20 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <EntitlementBanner />
        <ScrollProgress />
        {!hideHeader && !isEdgeMailClient && (
          <Header currentViewTitle={title} isSidebarCollapsed={!isSidebarExpanded} />
        )}
        {preferencesOpen && <Suspense fallback={null}><PreferencesModal /></Suspense>}
        <main
          data-lex-selectable="true"
          data-lex-selection-source={captureSurface}
          data-lex-selection-label={title || captureSurface.replace(/_/g, ' ')}
          data-lex-document-id={routeDocumentId}
          className={`relative flex-1 min-h-0 lex-page-content transition-colors duration-300 ${
            isEdgeDocsEditor || isEdgeMailClient || isCalendarPage || isLexRuntimeWorkspace || isAccountingSurface
              ? 'overflow-hidden' // Full remaining shell height; child owns internal scrolling
              : `overflow-y-auto p-4 sm:p-6 md:p-8 ${hideHeader ? '' : 'pt-16'}`
          }`}
        >
          <Suspense fallback={<div role="status" className="flex h-full items-center justify-center text-[var(--lex-text-secondary)]">Loading workspace…</div>}><Outlet /></Suspense>
        </main>
      </div>
    </div>
  );
}
